import MojitoCore from 'mojito/core';
import MojitoModules from 'mojito/modules';

import {icon} from '#core/utils/style-utils.js';
import {resolveBackground} from '#core/utils/color-utils.js';

import quantumConfig from '#core/config/quantum.js';
import genBetConfig from '#core/config/genbet.js';

import {generateParticipants} from '#core/application/modules/tiles/participants/index.js';
import {generatePivotsRow} from '#core/application/modules/tiles/pivot/index.js';
import {generatePriceBoostItem} from '#core/application/modules/tiles/price-boost-item/index.js';
import {generatePriceBoostLabel} from '#core/application/modules/tiles/price-boost-label/index.js';
import {generateSkeletonPart} from '#core/application/modules/tiles/skeleton-part/index.js';
import {generateSlider} from '#core/application/modules/tiles/range-input/index.js';
import {Cashout} from '#core/application/modules/cashout/index.js';
import {generateToggleButton} from '#core/application/modules/tiles/button/index.js';
import {generateEarlyPayoutIndicator} from '#core/application/modules/tiles/bonus-indicator/index.js';
import {generateBorder} from '#core/application/modules/tiles/border/index.js';
import {generateScrollButtonPane} from '#core/application/modules/tiles/scroll-button/index.js';

const merge = MojitoCore.Presentation.uiStyle.merge;

const SelectionButtonTypes = MojitoModules.SelectionButton.types;

export function marketsFactory(PALETTE) {
    const MARKETS_CONTENT_CONTAINER_STYLES = {
        ...resolveBackground(PALETTE.MARKETS_CONTAINER_BG),
        borderRadius: '0 0 8px 8px',
    };
    const MARKETS_PRICE_BOOST_LABEL = generatePriceBoostLabel(PALETTE.MARKETS_PRICE_BOOST_LABEL);
    const MARKETS_RANGE_INPUT = generateSlider(PALETTE.MARKETS_HANDICAP_RANGE_INPUT);

    const SHOW_ALL_SELECTIONS_BUTTON = {
        buttonLabel: {
            style: {
                ...PALETTE.MARKETS_SHOW_HIDE_ALL_BUTTON_TEXT,
            },
        },
        buttonIcon: {
            style: {
                fill: PALETTE.MARKETS_SHOW_HIDE_BUTTON_ICON_COLOR,
                minWidth: 8,
                minHeight: 8,
            },
        },
    };

    const CATEGORY_SELECTOR = {
        indicator: {
            style: {
                base: {
                    top: 'auto',
                    left: 4,
                    right: 4,
                },
            },
        },
        selected: {
            style: {
                base: {
                    minWidth: '0px',
                },
            },
        },
        unselected: {
            style: {
                base: {
                    minWidth: '0px',
                },
            },
        },
    };

    return merge(GENBET_BASED ? genBetConfig : quantumConfig, {
        AggregatedMarketPane: {
            expandable: {
                container: {
                    style: {
                        ...resolveBackground(PALETTE.MARKETS_CONTAINER_HEADER_BG),
                        ...generateBorder(PALETTE.MARKETS_CONTAINER_BORDER),
                    },
                },
                headerContainer: {
                    style: {
                        base: {
                            minHeight: '40px',
                        },
                    },
                },
                headerTextContainer: {
                    style: {
                        whiteSpace: 'normal',
                        ...PALETTE.MARKETS_CONTAINER_HEADER_TEXT,
                    },
                },
                rotation: {
                    image: {
                        style: {
                            fill: PALETTE.MARKETS_CONTAINER_HEADER_ROTATION_ICON_COLOR,
                        },
                    },
                },
                contentContainer: {
                    style: {
                        base: {
                            ...MARKETS_CONTENT_CONTAINER_STYLES,
                            padding: 0, // Reset default '0 4px 8px' from Mojito
                        },
                    },
                },
            },
        },
        AggregatedMarketGroup: {
            expandable: {
                container: {
                    style: {
                        ...resolveBackground(PALETTE.MARKETS_CONTAINER_HEADER_BG),
                        borderRadius: PALETTE.MARKETS_CONTAINER_BORDER_RADIUS,
                        boxShadow: PALETTE.MARKETS_CONTAINER_BOX_SHADOW,
                    },
                },
                rotation: {
                    image: {
                        style: {
                            fill: PALETTE.MARKETS_CONTAINER_HEADER_ROTATION_ICON_COLOR,
                        },
                    },
                },
                loadingSpinner: {
                    image: {
                        style: {
                            fill: PALETTE.MARKETS_CONTAINER_HEADER_SPINNER_ICON_COLOR,
                        },
                    },
                },
            },
            headerLabel: {
                style: PALETTE.MARKETS_CONTAINER_HEADER_TEXT,
            },
            subHeaderLabel: {
                style: PALETTE.MARKETS_CONTAINER_SUBHEADER_TEXT,
            },
        },
        MarketSignpostingIcons: {
            earlyPayoutIndicator: generateEarlyPayoutIndicator(PALETTE.MARKETS_CONTAINER_HEADER_EARLY_PAYOUT_INDICATOR),
            matchAccaIcon: merge(icon(PALETTE.MARKETS_CONTAINER_HEADER_BET_BUILDER_ICON_COLOR), {
                style: {
                    pointerEvents: 'none',
                },
            }),
            cashoutIcon: merge(icon(PALETTE.MARKETS_CONTAINER_HEADER_CASHOUT_ICON_COLOR), {
                style: {
                    pointerEvents: 'none',
                },
            }),
            showCashoutIcon: !!Cashout.enabled,
        },
        EventMarkets: {
            marketsContainer: {
                style: {
                    _mobile_padding: 8,
                },
            },
        },
        Market: {
            headerLabel: {
                style: {
                    ...PALETTE.MARKETS_SELECTION_HEADER_LABEL_TEXT,
                },
            },
            selectionsGroupContainer: {
                style: {
                    width: '100%',
                },
            },
            selectionButtonSkeleton: generateSkeletonPart(PALETTE.MARKET_SELECTION_BUTTON_SKELETON_PART),
        },
        SelectionHeaders: {
            label: {
                style: {
                    ...PALETTE.MARKETS_SELECTION_HEADER_LABEL_TEXT,
                },
            },
        },
        GenericMarketContent: {
            eachWayTermsLabel: {
                container: {
                    padding: '4px 8px 8px',
                },
                style: {
                    ...PALETTE.MARKET_SELECTION_EACH_WAY_TERMS_LABEL_TEXT,
                },
            },
            market: {
                headerLabel: {
                    style: {
                        ...PALETTE.MARKETS_SELECTION_HEADER_LABEL_TEXT,
                    },
                },
            },
            container: {
                style: {
                    ...MARKETS_CONTENT_CONTAINER_STYLES,
                },
            },
        },
        GameLineMarketContent: {
            container: {
                style: {
                    ...MARKETS_CONTENT_CONTAINER_STYLES,
                },
            },
            marketsContainer: {
                style: {
                    flexGrow: 1,
                },
            },
            participants: merge(generateParticipants(PALETTE.MARKETS_GAME_LINE_PARTICIPANTS), {
                container: {
                    style: {
                        justifyContent: 'space-around',
                        height: 'auto',
                    },
                    itemSpacing: 0,
                },
                participantItem: {
                    textWrapper: {
                        style: {
                            justifyContent: 'center',
                        },
                    },
                    startingPitcherName: {
                        container: {
                            flexGrow: 0,
                        },
                    },
                },
            }),
        },
        MarketLineSwitcher: {
            menuRow: {
                item: generateToggleButton(PALETTE.MARKETS_MARKET_LINE_SWITCHER, true),
            },
        },
        SetScoreContent: {
            labelText: {
                style: PALETTE.MARKET_SET_SCORE_LABEL_TEXT,
            },
            container: {
                style: {
                    ...MARKETS_CONTENT_CONTAINER_STYLES,
                },
            },
        },
        MarketLinesContent: {
            participantLabel: {
                style: PALETTE.MARKET_LINES_LABEL_TEXT,
            },
            marketsContainer: {
                style: {
                    ...MARKETS_CONTENT_CONTAINER_STYLES,
                },
            },
        },
        GameLineSkeleton: {
            container: {
                style: {
                    ...MARKETS_CONTENT_CONTAINER_STYLES,
                    padding: '32px 8px 8px',
                },
            },
            selectionSkeleton: generateSkeletonPart(PALETTE.MARKETS_SKELETON_CONTAINER_SELECTION_PART),
        },
        GoalScorerMarketContent: {
            container: {
                style: {
                    ...MARKETS_CONTENT_CONTAINER_STYLES,
                },
            },
            marketLinesSwitcherMenu: merge(generatePivotsRow(PALETTE.MARKETS_GOAL_SCORERS_CATEGORIES_SELECTOR), {
                item: {
                    unselected: {
                        style: {
                            base: {
                                flex: 'unset',
                            },
                        },
                    },
                    selected: {
                        style: {
                            base: {
                                flex: 'unset',
                            },
                        },
                    },
                },
            }),
        },
        PriceBoostAggregatedMarket: {
            aggregatedMarketPane: {
                expandable: {
                    container: {
                        style: {
                            padding: 0,
                        },
                    },
                    headerContainer: {
                        style: {
                            base: {
                                padding: '0 8px',
                            },
                        },
                    },
                    contentContainer: {
                        style: {
                            base: {
                                padding: '0 8px',
                            },
                        },
                    },
                },
            },
            headerContainer: {
                style: {
                    justifyContent: 'space-between',
                },
            },
            titleContainer: {
                itemSpacing: 4,
                style: {
                    maxWidth: 'fit-content',
                    padding: '0 4px 0 0',
                },
            },
            titleIcon: MARKETS_PRICE_BOOST_LABEL.icon,
            titleText: MARKETS_PRICE_BOOST_LABEL.label,
            marketContent: {
                priceBoostItem: generatePriceBoostItem(PALETTE.MARKETS_PRICE_BOOST_ITEM),
                container: {
                    separator: {
                        style: {
                            color: PALETTE.MARKETS_PRICE_BOOST_ITEM_DIVIDER_COLOR,
                            size: 1,
                        },
                    },
                },
            },
        },
        CorrectScoreMarketContent: {
            categorySelector: merge(generatePivotsRow(PALETTE.MARKETS_CORRECT_SCORE_MAIN_ALL_TAB_SELECTOR), {
                item: CATEGORY_SELECTOR,
            }),
            contentContainer: {
                style: {
                    ...MARKETS_CONTENT_CONTAINER_STYLES,
                },
            },
            // main tab
            mainContainer: {
                separator: {
                    style: {
                        color: PALETTE.MARKETS_CORRECT_SCORE_PARTICIPANTS_DIVIDER_COLOR,
                    },
                },
            },
            participantName: {
                style: {
                    ...PALETTE.MARKETS_CORRECT_SCORE_PARTICIPANTS.PARTICIPANTS_TEXT,
                },
            },
            correctScoreNumber: {
                style: {
                    ...PALETTE.MARKETS_CORRECT_SCORE_NUMBER_TEXT,
                },
            },
            selectionButtonContainer: {
                style: {
                    alignItems: 'flex-end',
                },
            },
            plusButtonEnabled: {
                buttonIcon: icon(PALETTE.MARKETS_CORRECT_SCORE_PLUS_MINUS_BUTTON_ICON_COLOR, 'plus-button'),
            },
            minusButtonEnabled: {
                buttonIcon: icon(PALETTE.MARKETS_CORRECT_SCORE_PLUS_MINUS_BUTTON_ICON_COLOR, 'minus-button'),
            },
            plusButtonDisabled: {
                buttonIcon: icon(PALETTE.MARKETS_CORRECT_SCORE_PLUS_MINUS_DISABLED_BUTTON_ICON_COLOR, 'plus-button'),
            },
            minusButtonDisabled: {
                buttonIcon: icon(PALETTE.MARKETS_CORRECT_SCORE_PLUS_MINUS_DISABLED_BUTTON_ICON_COLOR, 'minus-button'),
            },
            // list tab
            layoutDescriptors: {
                default: {
                    selectionNameDisplay: SelectionButtonTypes.NAME_DISPLAY.SHOW_SHORT,
                },
            },
            market: {
                selectionButton: {
                    view: {
                        layoutDirection: 'horizontalAlignCenter',
                    },
                },
            },
        },
        HandicapMarketContent: {
            isMainPivotDefault: !!GENBET_BASED,
            container: {
                style: {
                    ...MARKETS_CONTENT_CONTAINER_STYLES,
                },
            },
            genericMarketContent: {
                layoutDescriptors: {
                    default: {
                        selectionNameDisplay: SelectionButtonTypes.NAME_DISPLAY.SHOW_SHORT,
                    },
                },
                market: {
                    selectionButton: {
                        view: {
                            layoutDirection: 'horizontalAlignCenter',
                            _mobile_layoutDirection: 'horizontalAlignCenter',
                        },
                    },
                },
            },
            // main tab
            categorySelector: merge(generatePivotsRow(PALETTE.MARKETS_CORRECT_SCORE_MAIN_ALL_TAB_SELECTOR), {
                item: CATEGORY_SELECTOR,
            }),
            rangeInputLabel: {
                style: {
                    ...PALETTE.MARKETS_HANDICAP_RANGE_INPUT.RANGE_LABEL_TEXT,
                },
            },
            rangeInput: MARKETS_RANGE_INPUT.component,
        },
        PlayerInfo: {
            playerNameLabel: {
                container: {
                    flexGrow: 0,
                },
                style: {
                    ...PALETTE.MARKETS_PLAYER_NAME_TEXT,
                },
            },
            teamNameLabel: {
                container: {
                    flexGrow: 0,
                },
                style: {
                    ...PALETTE.MARKETS_TEAM_NAME_TEXT,
                },
            },
            marketNameLabel: {
                style: {
                    ...PALETTE.MARKETS_MARKET_NAME_TEXT,
                },
            },
        },
        PlayerSelectionsGrid: {
            container: {
                style: {
                    ...MARKETS_CONTENT_CONTAINER_STYLES,
                },
            },
            columnTitle: {
                style: {
                    ...PALETTE.MARKETS_SELECTION_HEADER_LABEL_TEXT,
                },
            },
            scrollContainer: generateScrollButtonPane(PALETTE.MARKETS_MILESTONE_SCROLL_BUTTON),
            marketsGrid: {
                style: {
                    flexBasis: 'auto',
                },
            },
            columnTitleCell: {
                style: {
                    height: 'initial',
                    alignSelf: 'center',
                },
            },
        },
        ShowAllSelectionsButton: {
            showAllButton: SHOW_ALL_SELECTIONS_BUTTON,
            hideAllButton: SHOW_ALL_SELECTIONS_BUTTON,
        },
        EventMarketsSkeleton: {
            marketContainer: {
                style: {
                    ...resolveBackground(PALETTE.MARKETS_CONTAINER_BG),
                    ...generateBorder(PALETTE.MARKETS_CONTAINER_BORDER),
                    padding: 0,
                },
            },
            marketHeaderContainer: {
                style: {
                    ...resolveBackground(PALETTE.MARKETS_SKELETON_CONTAINER_HEADER_BG),
                    padding: '12px 8px',
                    borderRadius: '8px 8px 0 0',
                },
            },
            marketHeaderPlaceholder: generateSkeletonPart(PALETTE.MARKETS_SKELETON_CONTAINER_HEADER_PART),
            selectionsContainer: {
                style: {
                    ...MARKETS_CONTENT_CONTAINER_STYLES,
                    padding: 8,
                },
            },
            selectionPlaceholder: generateSkeletonPart(PALETTE.MARKETS_SKELETON_CONTAINER_SELECTION_PART),
        },
    });
}
